"use client";

import { Minus, Plus } from "lucide-react";
import { cn } from "@/lib/utils";

interface QuantityStepperProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  disabled?: boolean;
  className?: string;
}

export function QuantityStepper({
  value,
  onChange,
  min = 1,
  max = 99,
  disabled = false,
  className,
}: QuantityStepperProps) {
  const decrementDisabled = disabled || value <= min;
  const incrementDisabled = disabled || value >= max;

  const buttonClasses =
    "inline-flex h-11 w-11 items-center justify-center text-muted transition-colors enabled:hover:bg-page enabled:hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:text-muted";

  return (
    <div
      role="group"
      aria-label="Quantity"
      className={cn(
        "inline-flex items-center rounded-md border border-line-strong bg-panel",
        className,
      )}
    >
      <button
        type="button"
        onClick={() => onChange(Math.max(min, value - 1))}
        disabled={decrementDisabled}
        aria-label="Decrease quantity"
        className={cn(buttonClasses, "rounded-l-md")}
      >
        <Minus className="h-4 w-4" aria-hidden="true" />
      </button>
      <span
        aria-live="polite"
        className="w-12 text-center text-sm font-medium text-ink"
      >
        {value}
      </span>
      <button
        type="button"
        onClick={() => onChange(Math.min(max, value + 1))}
        disabled={incrementDisabled}
        aria-label="Increase quantity"
        className={cn(buttonClasses, "rounded-r-md")}
      >
        <Plus className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}
